/**
 * useVisaRecommenderPersistence
 * ────────────────────────────────────────────────────────────────────────
 * Reads/writes the latest "Best Country for Me" quiz answers in localStorage
 * so downstream forms (consultation, profile-evaluation) can auto-fill
 * country / qualification / experience fields.
 *
 * Stored shape (JSON):
 *   {
 *     country: 'germany' | 'australia' | 'canada' | 'ireland',
 *     budget:  'low' | 'mid' | 'high',
 *     course:  'stem' | 'business' | 'healthcare' | 'humanities',
 *     goal:    'pr' | 'eu' | 'work' | 'return',
 *     savedAt: ISO 8601 string,
 *   }
 *
 * SECURITY NOTE
 * ─────────────
 * Same rationale as `useEcosystemPreference`: these are 5-character UX
 * preferences (no PII, no credentials, no auth tokens). localStorage is the
 * correct, accessible-everywhere store. XSS exposure of these keys would
 * reveal nothing an attacker couldn't infer from the URL the user is on.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'zafco_visa_recommender_v1';

const SAFE_VALUES = {
  country: new Set(['germany', 'australia', 'canada', 'ireland']),
  budget:  new Set(['low', 'mid', 'high']),
  course:  new Set(['stem', 'business', 'healthcare', 'humanities']),
  goal:    new Set(['pr', 'eu', 'work', 'return']),
};

const sanitize = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const out = {};
  for (const k of Object.keys(SAFE_VALUES)) {
    if (SAFE_VALUES[k].has(raw[k])) out[k] = raw[k];
  }
  // Only return when there is at least one valid field
  return Object.keys(out).length > 0 ? out : null;
};

const readPreference = () => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return sanitize(JSON.parse(raw));
  } catch {
    return null;
  }
};

const writePreference = (next) => {
  try {
    if (typeof window === 'undefined') return;
    const clean = sanitize(next);
    if (!clean) return;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...clean, savedAt: new Date().toISOString() }),
    );
  } catch {
    // Storage may be blocked (private mode) — fail silently per spec.
  }
};

/**
 * Read-only hook returning the latest stored preference + a clear() helper.
 * Components that *write* preferences should call `writeVisaRecommendation()`
 * directly so consumers don't accidentally trigger a useState rerender.
 */
export const useVisaRecommenderPreference = () => {
  const [pref, setPref] = useState(() => readPreference());

  // Pick up cross-tab updates
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setPref(readPreference());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const clear = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      setPref(null);
      // Dispatch a synthetic storage event so other ConsultationForm instances
      // on the same page (hero + bottom CTA) also re-read and hide their banners.
      window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY, newValue: null }));
    } catch {
      /* ignore */
    }
  }, []);

  return { pref, clear };
};

export const writeVisaRecommendation = writePreference;

/* ─── Mapping helpers — convert internal slugs to per-form values ──────── */

// ConsultationForm: country dropdown uses display labels.
// Ireland isn't in its options, so we fall back to "Other European Countries".
const COUNTRY_TO_FORM_LABEL = {
  germany:   'Germany',
  australia: 'Australia',
  canada:    'Canada',
  ireland:   'Other European Countries',
};

export const recommendationToFormFields = (pref) => {
  if (!pref) return null;
  const fields = {};
  if (pref.country && COUNTRY_TO_FORM_LABEL[pref.country]) {
    fields.country = COUNTRY_TO_FORM_LABEL[pref.country];
  }
  if (pref.country) fields.service_type = 'Study Abroad Placement';
  return Object.keys(fields).length > 0 ? fields : null;
};

// BookCounsellingPage: <select> values are capitalised country names.
// Ireland is a first-class option here, so it maps cleanly.
const COUNTRY_TO_BOOK_COUNSEL = {
  germany:   'Germany',
  australia: 'Australia',
  canada:    'Canada',
  ireland:   'Ireland',
};

export const recommendationToBookCounselFields = (pref) => {
  if (!pref?.country) return null;
  const dest = COUNTRY_TO_BOOK_COUNSEL[pref.country];
  return dest ? { destination: dest } : null;
};

// FreeProfileEvaluationPage: destination uses lowercase slugs that match
// our recommender 1:1 (germany / australia / canada / ireland).
// Budget maps from recommender's 3-tier scale to evaluation's 4-tier:
//   low  (Under €15K)     → '5-10'   (₹5–10 Lakhs)
//   mid  (€15K–€25K)      → '10-20'  (₹10–20 Lakhs)
//   high (€25K+)          → 'gt20'   (₹20 Lakhs+)
const BUDGET_TO_EVALUATION = { low: '5-10', mid: '10-20', high: 'gt20' };

export const recommendationToEvaluationFields = (pref) => {
  if (!pref) return null;
  const fields = {};
  // Only auto-fill destination if it's one of the 4 recommender countries
  // (the evaluation page also supports UK / NZ / UAE that the quiz doesn't cover).
  if (['germany', 'australia', 'canada', 'ireland'].includes(pref.country)) {
    fields.destination = pref.country;
  }
  if (pref.budget && BUDGET_TO_EVALUATION[pref.budget]) {
    fields.budget = BUDGET_TO_EVALUATION[pref.budget];
  }
  return Object.keys(fields).length > 0 ? fields : null;
};
